"use client";

/**
 * GenresTagInput — display current genres as removable chips and let the user
 * add new genres via an autocomplete input backed by existing DB genres.
 *
 * Chips are drag-to-reorder via the HTML5 DnD API. The first chip is always
 * shown with a "Primary" badge so admins know which genre appears on the home
 * page and artist listing cards.
 */

import { useState, useEffect, useId, KeyboardEvent, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, GripVertical } from "lucide-react";
import { db } from "@/lib/firebase";
import { fetchAllGenresFromDB, invalidateGenreCache } from "@/lib/genres";
import { cn } from "@/lib/utils";
import { useCallback } from "react";

interface GenresTagInputProps {
  /** Current array of genre strings */
  value: string[];
  onChange: (genres: string[]) => void;
  placeholder?: string;
  className?: string;
}

export default function GenresTagInput({
  value,
  onChange,
  placeholder = "Search or type a genre…",
  className,
}: GenresTagInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Drag state
  const dragIndex = useRef<number | null>(null);
  const dragOverIndex = useRef<number | null>(null);

  useEffect(() => {
    fetchAllGenresFromDB(db).then(setSuggestions);
  }, []);

  const addGenre = useCallback((raw: string) => {
    const genre = raw.trim();
    if (!genre) return;
    if (!value.includes(genre)) {
      onChange([...value, genre]);
      // Invalidate shared cache + add to local suggestion list immediately
      if (!suggestions.includes(genre)) {
        invalidateGenreCache();
        const updated = [...suggestions, genre].sort();
        setSuggestions(updated);
      }
    }
    setInputValue("");
    setShowDropdown(false);
  }, [value, onChange, suggestions]);

  const removeGenre = (genre: string) => {
    onChange(value.filter((g) => g !== genre));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addGenre(inputValue);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      removeGenre(value[value.length - 1]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v.includes(",")) {
      const parts = v.split(",");
      parts.slice(0, -1).forEach((p) => addGenre(p));
      setInputValue(parts[parts.length - 1]);
    } else {
      setInputValue(v);
      setShowDropdown(true);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Delay so click on dropdown option fires first
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
        if (inputValue.trim()) addGenre(inputValue);
      }
    }, 150);
  };

  const filtered = suggestions
    .filter((s) => !value.includes(s) && s.toLowerCase().includes(inputValue.toLowerCase()))
    .slice(0, 10);

  // ── Drag-to-reorder handlers ──────────────────────────────────────────────
  const handleDragStart = (idx: number) => {
    dragIndex.current = idx;
  };

  const handleDragEnter = (idx: number) => {
    dragOverIndex.current = idx;
  };

  const handleDragEnd = () => {
    const from = dragIndex.current;
    const to = dragOverIndex.current;
    if (from === null || to === null || from === to) {
      dragIndex.current = null;
      dragOverIndex.current = null;
      return;
    }
    const next = [...value];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
    dragIndex.current = null;
    dragOverIndex.current = null;
  };

  return (
    <div ref={containerRef} className={cn("space-y-1.5", className)}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((genre, idx) => (
            <Badge
              key={genre}
              variant={idx === 0 ? "default" : "secondary"}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragEnter={() => handleDragEnter(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDragEnd={handleDragEnd}
              className={cn(
                "gap-1 text-xs pr-1 cursor-grab active:cursor-grabbing select-none",
                idx === 0 ? "bg-foreground text-background" : ""
              )}
            >
              <GripVertical className="h-3 w-3 opacity-40 flex-shrink-0" />
              {genre}
              {idx === 0 && (
                <span className="opacity-60 text-[10px] font-normal ml-0.5">(primary)</span>
              )}
              <button
                type="button"
                className="ml-0.5 hover:text-destructive transition-colors"
                onClick={() => removeGenre(genre)}
                aria-label={`Remove ${genre}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <div className="relative">
        <Input
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowDropdown(true)}
          onBlur={handleBlur}
          placeholder={value.length === 0 ? placeholder : "Add another genre…"}
          className="h-8 text-sm"
          autoComplete="off"
        />
        {showDropdown && filtered.length > 0 && (
          <ul className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md max-h-48 overflow-y-auto">
            {filtered.map((g) => (
              <li key={g}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); addGenre(g); }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                >
                  {g}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground">
        Drag chips to reorder · First genre is primary · Click suggestion or press Enter to add
      </p>
    </div>
  );
}
